import { Component, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { PrinterState, printerFeatureKey } from '../../store/printer/printer.reducer';
import { ignorePrinter, resetPrinterIgnore } from '../../store/printer/printer.actions';
import { PrinterService } from '../../shared/services/printer.service';

@Component({
  selector: 'app-printer',
  templateUrl: './printer.component.html',
  styleUrls: ['./printer.component.scss']
})
export class PrinterComponent implements OnInit {
  private device: USBDevice | null = null;
  private endpointNumber: number | null = null;
  private readonly ESC = 0x1b;
  private readonly GS = 0x1d;
  private readonly LF = 0x0a;
  private readonly CUT_PAPER = new Uint8Array([this.GS, 0x56, 0x41, 0x00]);
  private readonly OPEN_DRAWER = new Uint8Array([this.ESC, 0x70, 0x00, 0x19, 0xFA]);
  private readonly CHARACTERS_PER_LINE = 48;
  isConnected = false;
  isPrinterIgnored = false;
  showPrinterModal = false;

  constructor(
    private store: Store<{ printer: PrinterState }>,
    private printerService: PrinterService
  ) {
    console.log('PrinterComponent: Constructor iniciado');

    // Suscribirse al estado de la impresora ignorada
    this.store.pipe(
      select(printerFeatureKey),
      select('isIgnored')
    ).subscribe(isIgnored => {
      this.isPrinterIgnored = isIgnored;
      console.log('PrinterComponent: Estado de impresora ignorada actualizado:', isIgnored);
    });

    // Inicializar el modal como visible por defecto
    setTimeout(() => {
      if (!localStorage.getItem('printerDevice') ||
          localStorage.getItem('printerIgnored') !== 'true') {
        console.log('PrinterComponent: Mostrando modal en constructor');
        this.showPrinterModal = true;
      }
    }, 500);
  }

  ngOnInit(): void {
    // Verificar si la impresora está ignorada en localStorage
    const printerInfoString = localStorage.getItem('printerDevice');
    if (printerInfoString) {
      try {
        const printerInfo = JSON.parse(printerInfoString);
        if (printerInfo.ignored) {
          // Si la impresora está ignorada, actualizar el estado en el store
          this.store.dispatch(ignorePrinter());
          console.log('Impresora ignorada encontrada en localStorage. No se intentará la conexión automática.');
          return;
        }
      } catch (e) {
        console.error('Error al procesar la información de la impresora guardada:', e);
        // Si hay un error al procesar la información, mostrar el modal
        this.showPrinterModal = true;
        return;
      }
    } else {
      // Si no hay información de impresora guardada, mostrar el modal directamente
      console.log('No hay información de impresora guardada. Mostrando modal.');
      this.showPrinterModal = true;
      console.log('Estado del modal:', this.showPrinterModal);
      return;
    }

    // También verificar si hay un estado de ignorado independiente
    const printerIgnored = localStorage.getItem('printerIgnored');
    if (printerIgnored === 'true') {
      // Si la impresora está ignorada, actualizar el estado en el store
      this.store.dispatch(ignorePrinter());
      console.log('Estado de impresora ignorada encontrado en localStorage. No se intentará la conexión automática.');
      return;
    }

    // Si la impresora no está ignorada, intentar conectar
    this.connectToDevice();
  }

  /**
   * Cierra el modal de impresora y continúa con la operación normal
   */
  closePrinterModal(): void {
    this.showPrinterModal = false;
    // Intentar conectar la impresora manualmente
    this.requestDevice();
  }

  async connectToDevice(): Promise<void> {
    console.log('PrinterComponent: Iniciando connectToDevice...');
    const printerInfoString = localStorage.getItem('printerDevice');
    console.log('PrinterComponent: Información de impresora guardada en localStorage:', printerInfoString);

    if (printerInfoString) {
      try {
        const printerInfo = JSON.parse(printerInfoString);
        const devices = await (navigator as any).usb.getDevices();

        // Buscar el dispositivo usando múltiples identificadores
        const storedDevice = devices.find((device: USBDevice) => {
          // Verificar si estamos usando el formato antiguo (solo printerId)
          if (printerInfo.printerId) {
            return device.serialNumber === printerInfo.printerId ||
                   device.productName === printerInfo.printerId;
          }

          // Usar el nuevo formato con múltiples identificadores
          return (
            // Coincidencia exacta por vendorId y productId (más confiable)
            (device.vendorId === printerInfo.vendorId && device.productId === printerInfo.productId) ||
            // Coincidencia por serialNumber si está disponible
            (printerInfo.serialNumber && device.serialNumber === printerInfo.serialNumber) ||
            // Coincidencia por productName si está disponible y no es "Dispositivo desconocido"
            (printerInfo.productName &&
             device.productName &&
             device.productName !== 'Dispositivo desconocido' &&
             device.productName === printerInfo.productName) ||
            // Coincidencia por uniqueId como último recurso
            `vid_${device.vendorId}_pid_${device.productId}` === printerInfo.uniqueId
          );
        });

        if (storedDevice) {
          console.log('PrinterComponent: Dispositivo encontrado:', storedDevice);
          await storedDevice.open();
          await this.selectConfigurationAndInterface(storedDevice);
          await this.reset(storedDevice);
          this.device = storedDevice;
          this.isConnected = true;
          console.log('Reconnected to the stored device:', storedDevice.productName || `vid_${storedDevice.vendorId}_pid_${storedDevice.productId}`);
          alert('Reconnected to the stored device.');
          return;
        } else {
          console.log('PrinterComponent: Dispositivo no encontrado en la lista de dispositivos.');
          // Mostrar modal para preguntar si desea ignorar la impresora
          this.showPrinterModal = true;
        }
      } catch (error) {
        console.error('Failed to reconnect to the stored device:', error);
        // Mostrar modal para preguntar si desea ignorar la impresora
        this.showPrinterModal = true;
      }
    } else {
      console.log('PrinterComponent: No hay dispositivo guardado en localStorage.');
      // Mostrar modal para preguntar si desea ignorar la impresora
      this.showPrinterModal = true;
    }
    this.isConnected = false;
  }

  async requestDevice(): Promise<void> {
    try {
      // Si la impresora estaba ignorada, resetear ese estado
      if (this.isPrinterIgnored) {
        this.resetPrinterIgnore();
      }

      // Cerrar el modal si está abierto
      if (this.showPrinterModal) {
        console.log('Cerrando modal antes de solicitar dispositivo USB');
        this.showPrinterModal = false;
      }

      console.log('PrinterComponent: Solicitando dispositivo USB...');
      const device = await (navigator as any).usb.requestDevice({ filters: [] });
      console.log('PrinterComponent: Dispositivo USB solicitado:', device);

      console.log('PrinterComponent: Abriendo dispositivo USB...');
      await device.open();
      console.log('PrinterComponent: Dispositivo USB abierto:', device);

      console.log('PrinterComponent: Seleccionando configuración e interfaz...');
      await this.selectConfigurationAndInterface(device);
      console.log('PrinterComponent: Configuración e interfaz seleccionadas:', device);

      console.log('PrinterComponent: Reseteando dispositivo...');
      await this.reset(device);
      console.log('PrinterComponent: Dispositivo reseteado:', device);

      this.device = device;
      if (this.device) {
        // Guardar la información completa del dispositivo en localStorage
        const printerInfo = {
          // Identificadores principales
          productName: device.productName || 'Dispositivo sin nombre',
          serialNumber: device.serialNumber || '',
          vendorId: device.vendorId,
          productId: device.productId,
          // Identificador único compuesto (para casos donde no hay nombre ni serial)
          uniqueId: `vid_${device.vendorId}_pid_${device.productId}`,
          // Timestamp para referencia
          lastConnected: new Date().toISOString(),
          // Asegurarse de que no esté ignorada
          ignored: false
        };

        localStorage.setItem('printerDevice', JSON.stringify(printerInfo));
        console.log('PrinterComponent: Configuración completa guardada en localStorage:', JSON.stringify(printerInfo));
        console.log('Connected to:', device.productName || printerInfo.uniqueId);
        alert('Connected to the printer.');
        this.isConnected = true;
      }
    } catch (error) {
      console.error('Failed to connect to the device:', error);
      alert('Failed to connect to the printer.');
      this.isConnected = false;
    }
  }

  async selectConfigurationAndInterface(device: USBDevice): Promise<void> {
    const configuration = device.configurations[0];
    await device.selectConfiguration(configuration.configurationValue);
    for (const iface of configuration.interfaces) {
      for (const alt of iface.alternates) {
        try {
          await device.claimInterface(iface.interfaceNumber);
          await device.selectAlternateInterface(iface.interfaceNumber, alt.alternateSetting);
          console.log(`Claimed and selected interface number: ${iface.interfaceNumber}, alternate setting: ${alt.alternateSetting}`);
          for (const endpoint of alt.endpoints) {
            if (endpoint.direction === 'out') {
              this.endpointNumber = endpoint.endpointNumber;
              console.log(`Endpoint OUT found: ${this.endpointNumber}`);
              return;
            }
          }
        } catch (error) {
          console.error(`Failed to claim and select interface number: ${iface.interfaceNumber}, alternate setting: ${alt.alternateSetting}`, error);
        }
      }
    }
    throw new Error('Unable to claim and select any interface.');
  }

  async reset(device: USBDevice): Promise<void> {
    try {
      await this.sendBytes(device, new Uint8Array([this.ESC, 0x40]));
    } catch (error) {
      console.log(error);
    }
  }

  async sendText(device: USBDevice, str: string): Promise<void> {
    const bytes = new Uint8Array(this.stringToBytes(str));
    await this.sendBytes(device, bytes);
  }

  async sendBytes(device: USBDevice, bytes: Uint8Array): Promise<void> {
    if (this.endpointNumber !== null) {
      await device.transferOut(this.endpointNumber, bytes);
    } else {
      throw new Error('Endpoint number is not set.');
    }
  }

  async setCharacterStyle(device: USBDevice, style: { smallFont?: boolean; emphasized?: boolean; doubleHeight?: boolean; doubleWidth?: boolean; underline?: boolean } = {}): Promise<void> {
    let v = 0;
    if (style.smallFont) v |= 1 << 0;
    if (style.emphasized) v |= 1 << 3;
    if (style.doubleHeight) v |= 1 << 4;
    if (style.doubleWidth) v |= 1 << 5;
    if (style.underline) v |= 1 << 7;
    await this.sendBytes(device, new Uint8Array([this.ESC, 0x21, v]));
  }

  charToByte(char: string): number {
    return char.charCodeAt(0);
  }

  stringToBytes(str: string): number[] {
    return Array.from(str).reduce((prev: number[], char) => {
      prev.push(this.charToByte(char));
      return prev;
    }, []);
  }

  centerText(text: string): string {
    const spaces = Math.floor((this.CHARACTERS_PER_LINE - text.length) / 2);
    return ' '.repeat(spaces) + text + ' '.repeat(spaces);
  }

  async printHeader(device: USBDevice): Promise<void> {
    const headerLines = [
      "CANGAS AVENTURA S.L",
      "FINCA DE LA DEHESA - EL PORTAZGO, COVIELLA",
      "33540 - CANGAS DE ONIS (ASTURIAS)",
      "TELÉFONO: 985 84 82 61"
    ];

    for (const line of headerLines) {
      await this.sendText(device, this.centerText(line) + '\n');
    }
  }

  async printSeparator(device: USBDevice): Promise<void> {
    const separator = '-'.repeat(this.CHARACTERS_PER_LINE) + '\n';
    await this.sendText(device, separator);
  }

  async printLines(): Promise<void> {
    if (!this.device) {
      alert("Please connect to the printer first.");
      return;
    }
    await this.printHeader(this.device);
    await this.printSeparator(this.device);
    await this.sendText(this.device, "Line 1: Hello, World!\n");
    await this.printSeparator(this.device);
    await this.sendText(this.device, "Line 2: This is a test.\n");
    await this.printSeparator(this.device);
    await this.sendText(this.device, "Line 3: Thank you!\n");
    await this.printSeparator(this.device);
  }

  formatLine(description: string, price: string): string {
    if (description.length + price.length > this.CHARACTERS_PER_LINE) {
      description = description.slice(0, this.CHARACTERS_PER_LINE - price.length - 1);
    }
    const spaces = this.CHARACTERS_PER_LINE - description.length - price.length;
    return description + ' '.repeat(spaces) + price;
  }

  async printTicket(items: { description: string; price: string }[]): Promise<void> {
    if (!this.device) {
      alert("Please connect to the printer first.");
      return;
    }
    await this.printHeader(this.device);
    await this.printSeparator(this.device);
    for (const item of items) {
      const line = this.formatLine(item.description, item.price);
      await this.sendText(this.device, line + '\n');
    }
    await this.printSeparator(this.device);
    await this.sendText(this.device, 'TOTAL: ' + items.reduce((acc, item) => acc + parseFloat(item.price), 0).toFixed(2) + '\n');
  }

  async openDrawer(): Promise<void> {
    if (this.device) {
      await this.sendBytes(this.device, this.OPEN_DRAWER);
      alert('¡Cajón abierto!');
    } else {
      alert("Conecta la impresora en primer lugar.");
    }
  }

  async cutPaper(): Promise<void> {
    if (this.device) {
      await this.sendBytes(this.device, this.CUT_PAPER);
      alert('Papel cortado. Por favor, recoge tu ticket');
    } else {
      alert("Conecta la impresora en primer lugar.");
    }
  }

  /**
   * Marca la impresora como ignorada
   * Esto guardará el estado en localStorage y lo mantendrá entre sesiones
   */
  ignorePrinter(): void {
    // Actualizar el estado en el store
    this.store.dispatch(ignorePrinter());

    // Actualizar también la información en el objeto printerDevice
    const printerInfoString = localStorage.getItem('printerDevice');
    if (printerInfoString) {
      try {
        const printerInfo = JSON.parse(printerInfoString);
        printerInfo.ignored = true;
        localStorage.setItem('printerDevice', JSON.stringify(printerInfo));
      } catch (e) {
        console.error('Error al procesar la información de la impresora guardada:', e);
      }
    }

    // Llamar al servicio para marcar la impresora como ignorada
    this.printerService.ignorePrinter();

    // Actualizar el estado local
    this.isConnected = false;
    this.device = null;
    this.endpointNumber = null;

    // Cerrar el modal si está abierto
    this.showPrinterModal = false;

    alert('Impresora ignorada. No se intentará la reconexión automática.');
  }

  /**
   * Resetea el estado de impresora ignorada
   * Esto permitirá volver a conectar la impresora
   */
  resetPrinterIgnore(): void {
    // Actualizar el estado en el store
    this.store.dispatch(resetPrinterIgnore());

    // Actualizar también la información en el objeto printerDevice
    const printerInfoString = localStorage.getItem('printerDevice');
    if (printerInfoString) {
      try {
        const printerInfo = JSON.parse(printerInfoString);
        printerInfo.ignored = false;
        localStorage.setItem('printerDevice', JSON.stringify(printerInfo));
      } catch (e) {
        console.error('Error al procesar la información de la impresora guardada:', e);
      }
    }

    alert('Estado de impresora restablecido. Ahora puede conectar la impresora.');
  }
}

interface Navigator {
  usb: {
    requestDevice(options: { filters: any[] }): Promise<USBDevice>;
    getDevices(): Promise<USBDevice[]>;
  };
}

// Declara la interfaz USBDevice si no está disponible en el entorno actual
interface USBDevice {
  serialNumber: string;
  productName: string;
  vendorId: number;
  productId: number;
  configurations: USBConfiguration[];
  open(): Promise<void>;
  claimInterface(interfaceNumber: number): Promise<void>;
  selectConfiguration(configurationValue: number): Promise<void>;
  selectAlternateInterface(interfaceNumber: number, alternateSetting: number): Promise<void>;
  transferOut(endpointNumber: number, data: BufferSource): Promise<USBOutTransferResult>;
}

interface USBConfiguration {
  configurationValue: number;
  interfaces: USBInterface[];
}

interface USBInterface {
  interfaceNumber: number;
  alternates: USBAlternateInterface[];
  claimed: boolean;
}

interface USBAlternateInterface {
  alternateSetting: number;
  endpoints: USBEndpoint[];
}

interface USBEndpoint {
  endpointNumber: number;
  direction: 'in' | 'out';
}

interface USBOutTransferResult {
  status: string;
}
